/**
 * OPFS storage with versioned migration and an append-only operation log
 * shaped for CRDT sync (Automerge-compatible field naming, see TODO_v1).
 *
 * Sync semantics: last-writer-wins per top-level field. Operation log retains
 * full history so a future Automerge swap can rebuild state deterministically.
 */

export interface Op<T> {
  ts: number;
  actor: string;
  path: string[];
  value: T;
}

export interface OpLog<T> {
  v: 1;
  appId: string;
  ops: Op<T>[];
}

const ACTOR_KEY = 'aramgah:actor-id';

export function actorId(): string {
  let id = localStorage.getItem(ACTOR_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(ACTOR_KEY, id);
  }
  return id;
}

async function opfs(): Promise<FileSystemDirectoryHandle | null> {
  if (!('storage' in navigator) || !navigator.storage.getDirectory) return null;
  try {
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

export async function writeOpfs(name: string, payload: string): Promise<void> {
  const dir = await opfs();
  if (!dir) throw new Error('opfs-unavailable');
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  await writable.write(payload);
  await writable.close();
}

export async function readOpfs(name: string): Promise<string | null> {
  const dir = await opfs();
  if (!dir) return null;
  try {
    const handle = await dir.getFileHandle(name);
    const file = await handle.getFile();
    return await file.text();
  } catch {
    return null;
  }
}

export async function appendOp<T>(appId: string, op: Omit<Op<T>, 'actor' | 'ts'>): Promise<void> {
  const file = `${appId}-oplog.json`;
  const existing = await readOpfs(file);
  const log: OpLog<T> = existing ? JSON.parse(existing) as OpLog<T> : { v: 1, appId, ops: [] };
  log.ops.push({ ...op, ts: Date.now(), actor: actorId() });
  await writeOpfs(file, JSON.stringify(log));
}

export async function readOpLog<T>(appId: string): Promise<OpLog<T>> {
  const file = `${appId}-oplog.json`;
  const existing = await readOpfs(file);
  return existing ? JSON.parse(existing) as OpLog<T> : { v: 1, appId, ops: [] };
}

export async function pruneOpLog(appId: string, beforeTs: number): Promise<number> {
  const log = await readOpLog<unknown>(appId);
  const before = log.ops.length;
  log.ops = log.ops.filter((o) => o.ts >= beforeTs);
  await writeOpfs(`${appId}-oplog.json`, JSON.stringify(log));
  return before - log.ops.length;
}

export interface SyncManifest {
  v: 1;
  lastSync: number;
  remoteEtag: string | null;
}

export async function readSyncManifest(appId: string): Promise<SyncManifest> {
  const existing = await readOpfs(`${appId}-sync.json`);
  return existing ? JSON.parse(existing) as SyncManifest : { v: 1, lastSync: 0, remoteEtag: null };
}

export async function writeSyncManifest(appId: string, m: SyncManifest): Promise<void> {
  await writeOpfs(`${appId}-sync.json`, JSON.stringify(m));
}
